import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { Card } from '../shared/ui';
import { DateInput } from '../shared/DateInput';

type Locale = 'hr' | 'en';

type PtUser = { id: number; name: string; email: string; role: string };
type PtBranch = { id: number; code: string; name: string; company_id: number | null };
type PtCompany = { id: number; code: string; name: string };
type VoditeljBranch = { voditelj_user_id: number; branch_dept_id: number };
type VoditeljRegion = { voditelj_user_id: number; region: string };
type PtBankAccount = {
    id: number; company_id: number | null; branch_dept_id: number | null; iban: string;
    bank_name: string; account_label: string; purpose: string; lifecycle_status: string;
    is_active: number | boolean; is_fallback: number | boolean; is_buyback_default?: number | boolean; bank_code: string | null;
};
type PtBankCode = { id: number; code: string; name: string; short: string | null; is_active: number | boolean };

type ShowResp = {
    settings: Record<string, any>;
    users: PtUser[];
    roles: Record<string, string>;             // {user_id: role}
    voditelj_branches: VoditeljBranch[];
    voditelj_regions: VoditeljRegion[];
    regions: string[];
    branches: PtBranch[];
    companies: PtCompany[];
    bank_accounts: PtBankAccount[];
    bank_codes: PtBankCode[];
    reason_codes: string[];
};

const REASON_LABELS: Record<string, [string, string]> = {
    otkup_lom: ['Otkup loma', 'Scrap buy'],
    otkup_invest: ['Otkup investicijskog', 'Investment buy'],
    dnevna_gotovina: ['Dnevna gotovina', 'Daily cash'],
    virman_klijent: ['Virman klijentu', 'Client transfer'],
    upit_o_uplati: ['Upit o uplati', 'Payment inquiry'],
    ostalo: ['Ostalo', 'Other'],
};

const ROLE_LABELS: Record<string, [string, string]> = {
    user: ['Korisnik (poslovnica)', 'User (branch)'],
    voditelj: ['Voditelj', 'Lead'],
    manager: ['Manager (uprava)', 'Manager'],
    provjera_uplate: ['Samo provjera uplate', 'Payment check only'],
    none: ['— bez PT role (legacy) —', '— no PT role (legacy) —'],
};

/** Soap-bubble multiselect nad korisnicima: quick search → filtrirana lista → inline checkbox → chip. */
function UserMultiSelect({ users, selected, onChange, locale, placeholder }: {
    users: PtUser[]; selected: number[]; onChange: (ids: number[]) => void; locale: Locale; placeholder: string;
}) {
    const [q, setQ] = useState('');
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const sel = new Set(selected);
    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return [] as PtUser[];
        return users.filter((u) => u.name.toLowerCase().includes(needle) || (u.email ?? '').toLowerCase().includes(needle)).slice(0, 8);
    }, [q, users]);
    const toggle = (id: number) => onChange(sel.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    const chosen = users.filter((u) => sel.has(u.id));
    return (
        <div>
            {chosen.length > 0 ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    {chosen.map((u) => (
                        <span key={u.id} style={{ background: '#eef2ff', color: '#1e3a8a', borderRadius: 14, padding: '3px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {u.name}
                            <button type="button" onClick={() => toggle(u.id)} style={{ background: 'transparent', border: 0, color: '#1e3a8a', cursor: 'pointer', fontWeight: 800, lineHeight: 1 }}>×</button>
                        </span>
                    ))}
                </div>
            ) : null}
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box', fontSize: '0.84rem' }} />
            {filtered.length > 0 ? (
                <div style={{ border: '1px solid #e3e3e3', borderRadius: 6, marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                    {filtered.map((u) => (
                        <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3', fontSize: '0.82rem' }}>
                            <input type="checkbox" checked={sel.has(u.id)} onChange={() => toggle(u.id)} />
                            <span>{u.name}</span>
                            <span style={{ color: '#999', fontSize: '0.74rem' }}>{u.email}</span>
                        </label>
                    ))}
                </div>
            ) : q.trim() ? <div style={{ color: '#999', fontSize: '0.78rem', padding: '4px 2px' }}>{t('Nema rezultata', 'No results')}</div> : null}
        </div>
    );
}

/** Poslovnice multiselect: quick search po kodu/nazivu → chips. */
function BranchMultiSelect({ branches, selected, onChange, locale }: {
    branches: PtBranch[]; selected: number[]; onChange: (ids: number[]) => void; locale: Locale;
}) {
    const [q, setQ] = useState('');
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const sel = new Set(selected);
    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return [] as PtBranch[];
        return branches.filter((b) => b.name.toLowerCase().includes(needle) || (b.code ?? '').toLowerCase().includes(needle)).slice(0, 10);
    }, [q, branches]);
    const toggle = (id: number) => onChange(sel.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    const chosen = branches.filter((b) => sel.has(b.id));
    return (
        <div>
            {chosen.length > 0 ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    {chosen.map((b) => (
                        <span key={b.id} style={{ background: '#e8f5ee', color: '#1f5c34', borderRadius: 14, padding: '3px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {b.code ? `${b.code} · ` : ''}{b.name}
                            <button type="button" onClick={() => toggle(b.id)} style={{ background: 'transparent', border: 0, color: '#1f5c34', cursor: 'pointer', fontWeight: 800, lineHeight: 1 }}>×</button>
                        </span>
                    ))}
                </div>
            ) : null}
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Traži poslovnicu…', 'Search branch…')}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box', fontSize: '0.84rem' }} />
            {filtered.length > 0 ? (
                <div style={{ border: '1px solid #e3e3e3', borderRadius: 6, marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                    {filtered.map((b) => (
                        <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3', fontSize: '0.82rem' }}>
                            <input type="checkbox" checked={sel.has(b.id)} onChange={() => toggle(b.id)} />
                            <span>{b.code ? <code style={{ color: '#888' }}>{b.code}</code> : null} {b.name}</span>
                        </label>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

type Delegation = { id: number; from_voditelj_user_id: number; to_user_id: number; from_date: string; to_date: string; note: string | null; from_name: string | null; to_name: string | null; active: boolean };

/** Privremeno predavanje ovlasti (voditelj na godišnjem → zamjena odobrava isplate). */
function DelegationsCard({ token, locale, users, voditeljIds }: { token: string; locale: Locale; users: PtUser[]; voditeljIds: number[] }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<Delegation[]>([]);
    const [from, setFrom] = useState<number | ''>('');
    const [to, setTo] = useState<number | ''>('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const voditelji = useMemo(() => users.filter((u) => voditeljIds.includes(u.id)), [users, voditeljIds]);

    const reload = useCallback(async () => {
        try { const d = await api<{ rows: Delegation[] }>('/payment-traffic/delegations', token); setRows(d.rows ?? []); } catch { /* ignore */ }
    }, [token]);
    useEffect(() => { void reload(); }, [reload]);

    const add = async () => {
        setErr('');
        if (!from || !to || !fromDate || !toDate) { setErr(t('Popuni voditelja, zamjenu i period.', 'Fill lead, substitute and period.')); return; }
        setBusy(true);
        try {
            await mutate('/payment-traffic/delegations', token, 'POST', { from_voditelj_user_id: from, to_user_id: to, from_date: fromDate, to_date: toDate, note: note || null });
            setFrom(''); setTo(''); setFromDate(''); setToDate(''); setNote(''); await reload();
        } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
        finally { setBusy(false); }
    };
    const del = async (id: number) => {
        if (!confirm(t('Ukloniti delegaciju?', 'Remove delegation?'))) return;
        try { await mutate(`/payment-traffic/delegations/${id}`, token, 'DELETE'); await reload(); } catch { /* ignore */ }
    };
    const sel: React.CSSProperties = { padding: '6px 8px', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.82rem' };

    return (
        <Card>
            <h3 style={{ margin: '0 0 4px', color: '#6b4f1c' }}>🔑 {t('Privremeno predavanje ovlasti', 'Temporary approval delegation')}</h3>
            <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 10 }}>{t('Kad je voditelj na godišnjem, zamjena preuzima odobravanje isplata za njegove poslovnice u zadanom periodu.', 'When a lead is on vacation, the substitute takes over payout approvals for their branches in the given period.')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                <select value={from} onChange={(e) => setFrom(e.target.value ? Number(e.target.value) : '')} style={sel}>
                    <option value="">{t('Voditelj (predaje)', 'Lead (delegates)')}</option>
                    {voditelji.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                <span style={{ color: '#888' }}>→</span>
                <select value={to} onChange={(e) => setTo(e.target.value ? Number(e.target.value) : '')} style={sel}>
                    <option value="">{t('Zamjena (preuzima)', 'Substitute')}</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                <DateInput value={fromDate} onChange={setFromDate} style={{ ...sel, width: 130 }} />
                <span style={{ color: '#888' }}>–</span>
                <DateInput value={toDate} onChange={setToDate} style={{ ...sel, width: 130 }} />
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('napomena', 'note')} style={{ ...sel, width: 160 }} />
                <button type="button" disabled={busy} onClick={() => void add()} style={{ padding: '6px 14px', background: '#6b4f1c', color: '#fffbeb', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>{busy ? '…' : '＋ ' + t('Dodaj', 'Add')}</button>
            </div>
            {err ? <div style={{ color: '#b14040', fontSize: '0.8rem', marginBottom: 6 }}>{err}</div> : null}
            {rows.length === 0 ? <div style={{ color: '#999', fontSize: '0.82rem' }}>{t('Nema aktivnih delegacija.', 'No delegations.')}</div> : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} style={{ borderBottom: '1px solid #f0e9d5' }}>
                                <td style={{ padding: '5px 6px' }}>{r.active ? <span style={{ background: '#1f7a36', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: '0.7rem' }}>● {t('aktivno', 'active')}</span> : <span style={{ color: '#999', fontSize: '0.72rem' }}>{t('neaktivno', 'inactive')}</span>}</td>
                                <td style={{ padding: '5px 6px' }}><strong>{r.from_name}</strong> → {r.to_name}</td>
                                <td style={{ padding: '5px 6px', color: '#666' }}>{r.from_date?.split('-').reverse().join('.')}.–{r.to_date?.split('-').reverse().join('.')}.</td>
                                <td style={{ padding: '5px 6px', color: '#888' }}>{r.note}</td>
                                <td style={{ padding: '5px 6px', textAlign: 'right' }}><button type="button" onClick={() => void del(r.id)} style={{ background: 'transparent', border: 0, color: '#b14040', cursor: 'pointer', fontWeight: 800 }}>×</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Card>
    );
}

type SectionKey = 'ovlasti' | 'odobrenja' | 'racuni' | 'alarm';
const SECTIONS: Array<{ key: SectionKey; hr: string; en: string; subHr: string; subEn: string }> = [
    { key: 'ovlasti', hr: 'Uloge i ovlasti', en: 'Roles and authority', subHr: 'Uloge, voditelji, zamjene', subEn: 'Roles, leads, substitutes' },
    { key: 'odobrenja', hr: 'Odobravanje', en: 'Approvals', subHr: 'Tipovi zahtjeva, veliki otkupi, HNWI', subEn: 'Request types, large buys, HNWI' },
    { key: 'racuni', hr: 'Računi', en: 'Accounts', subHr: 'Poslovnice, isplata, banke', subEn: 'Branches, payouts, banks' },
    { key: 'alarm', hr: 'Alarm i kontrola', en: 'Alarm and control', subHr: 'Radno vrijeme, kontrola otkupa', subEn: 'Work hours, buy reconciliation' },
];

export function PaymentTrafficSettings({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<ShowResp | null>(null);
    const [err, setErr] = useState('');
    const [saved, setSaved] = useState('');
    const [roleSearch, setRoleSearch] = useState('');
    const [section, setSectionRaw] = useState<SectionKey>(() => {
        try { const v = window.localStorage.getItem('pt-settings-section'); if (v && SECTIONS.some((x) => x.key === v)) return v as SectionKey; } catch { /* ignore */ }
        return 'ovlasti';
    });
    const setSection = (k: SectionKey) => { setSectionRaw(k); try { window.localStorage.setItem('pt-settings-section', k); } catch { /* ignore */ } };

    const load = useCallback(async () => {
        try {
            const d = await api<ShowResp>('/payment-traffic/settings', token);
            setData(d);
            setErr('');
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
        }
    }, [token]);

    useEffect(() => { void load(); }, [load]);

    const flash = (msg: string) => { setSaved(msg); window.setTimeout(() => setSaved(''), 1800); };

    const saveSetting = useCallback(async (key: string, value: any) => {
        // optimistic
        setData((prev) => prev ? { ...prev, settings: { ...prev.settings, [key]: value } } : prev);
        try {
            await mutate('/payment-traffic/settings', token, 'PUT', { key, value });
            flash(t('Spremljeno', 'Saved'));
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            setErr(msg);
            alert('Greška pri spremanju postavke: ' + msg);   // vidljivo — da se dijagnosticira ako padne
            void load();
        }
    }, [token, load]);

    const setRole = useCallback(async (userId: number, role: string) => {
        setData((prev) => {
            if (!prev) return prev;
            const roles = { ...prev.roles };
            if (role === 'none') delete roles[String(userId)];
            else roles[String(userId)] = role;
            return { ...prev, roles };
        });
        try {
            await mutate('/payment-traffic/settings/role', token, 'POST', { user_id: userId, role });
            flash(t('Uloga spremljena', 'Role saved'));
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
            void load();
        }
    }, [token, load]);

    const syncVoditeljBranches = useCallback(async (voditeljId: number, branchIds: number[]) => {
        setData((prev) => {
            if (!prev) return prev;
            const others = prev.voditelj_branches.filter((v) => v.voditelj_user_id !== voditeljId);
            const mine = branchIds.map((bid) => ({ voditelj_user_id: voditeljId, branch_dept_id: bid }));
            return { ...prev, voditelj_branches: [...others, ...mine] };
        });
        try {
            await mutate('/payment-traffic/settings/voditelj-branches', token, 'PUT', { voditelj_user_id: voditeljId, branch_dept_ids: branchIds });
            flash(t('Poslovnice spremljene', 'Branches saved'));
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
            void load();
        }
    }, [token, load]);

    // Regionalni prečac: voditelj pokriva SVE poslovnice navedenih regija (i buduće), bez ručnog popisa.
    const syncVoditeljRegions = useCallback(async (voditeljId: number, regions: string[]) => {
        setData((prev) => {
            if (!prev) return prev;
            const others = prev.voditelj_regions.filter((v) => v.voditelj_user_id !== voditeljId);
            const mine = regions.map((region) => ({ voditelj_user_id: voditeljId, region }));
            return { ...prev, voditelj_regions: [...others, ...mine] };
        });
        try {
            await mutate('/payment-traffic/settings/voditelj-regions', token, 'PUT', { voditelj_user_id: voditeljId, regions });
            flash(t('Regije spremljene', 'Regions saved'));
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
            void load();
        }
    }, [token, load]);

    // P2: označi/makni is_fallback (zadani isplatni račun) — jedinstven po firmi (backend čisti ostale).
    const toggleFallback = useCallback(async (accId: number, companyId: number | null, next: boolean) => {
        setData((prev) => {
            if (!prev) return prev;
            const accs = prev.bank_accounts.map((a) => {
                if (a.id === accId) return { ...a, is_fallback: next ? 1 : 0 };
                if (next && companyId != null && a.company_id === companyId) return { ...a, is_fallback: 0 };
                return a;
            });
            return { ...prev, bank_accounts: accs };
        });
        try {
            await mutate(`/admin/bank-accounts/${accId}`, token, 'PATCH', { is_fallback: next });
            flash(t('Zadani račun spremljen', 'Default account saved'));
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
            void load();
        }
    }, [token, load]);

    const filteredUsers = useMemo(() => {
        if (!data) return [] as PtUser[];
        const rf = (u: PtUser) => data.roles[String(u.id)] ?? '';
        const needle = roleSearch.trim().toLowerCase();
        const base = needle ? data.users.filter((u) => u.name.toLowerCase().includes(needle) || (u.email ?? '').toLowerCase().includes(needle)) : data.users;
        // s postavljenom PT rolom prvo, pa ostali
        return [...base].sort((a, b) => {
            const ra = rf(a) ? 0 : 1; const rb = rf(b) ? 0 : 1;
            return ra !== rb ? ra - rb : a.name.localeCompare(b.name);
        });
    }, [data, roleSearch]);

    if (err && !data) return <Card><div style={{ color: '#b14040' }}>{err}</div></Card>;
    if (!data) return <Card><div style={{ color: '#888' }}>{t('Učitavanje postavki…', 'Loading settings…')}</div></Card>;

    const roleFor = (u: PtUser): string => data.roles[String(u.id)] ?? '';
    const voditelji = data.users.filter((u) => roleFor(u) === 'voditelj');
    const reqVoditelj = (data.settings.type_requires_voditelj ?? {}) as Record<string, boolean>;

    return (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <nav aria-label={t('Odjeljci postavki', 'Settings sections')} style={{ flex: '0 0 220px', position: 'sticky', top: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {SECTIONS.map((x) => {
                    const on = x.key === section;
                    return (
                        <button key={x.key} type="button" onClick={() => setSection(x.key)} aria-current={on ? 'page' : undefined}
                            style={{ textAlign: 'left', padding: '9px 12px', borderRadius: 8, border: 0, borderLeft: '3px solid ' + (on ? '#c9971c' : 'transparent'), background: on ? '#f4efe3' : 'transparent', color: '#2f3337', cursor: 'pointer', lineHeight: 1.25 }}>
                            <div style={{ fontSize: '0.88rem', fontWeight: on ? 800 : 600 }}>{t(x.hr, x.en)}</div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 400, color: '#888', marginTop: 2 }}>{t(x.subHr, x.subEn)}</div>
                        </button>
                    );
                })}
            </nav>
            <div style={{ flex: '1 1 600px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {saved ? <div style={{ position: 'fixed', top: 16, right: 16, background: '#1f7a36', color: '#fff', padding: '6px 14px', borderRadius: 8, zIndex: 200, fontSize: '0.82rem', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>✓ {saved}</div> : null}
            {err ? <div style={{ color: '#b14040', fontSize: '0.82rem' }}>{err}</div> : null}

            {section === 'ovlasti' ? (<>
            {/* ── Uloge ─────────────────────────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>👥 {t('Uloge', 'Roles')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>
                    {t('Voditelj odobrava zahtjeve SVOJIH poslovnica; manager izvršava isplate. Bez postavljene role vrijedi legacy (manager/admin → manager).',
                        'Lead approves requests of THEIR branches; manager executes payouts. Without a role, legacy applies (manager/admin → manager).')}
                </div>
                <input type="text" value={roleSearch} onChange={(e) => setRoleSearch(e.target.value)} placeholder={t('Brzo traži korisnika…', 'Quick search user…')}
                    style={{ width: '100%', maxWidth: 360, padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, marginBottom: 10, fontSize: '0.84rem' }} />
                <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                    {filteredUsers.slice(0, 60).map((u) => {
                        const r = roleFor(u);
                        return (
                            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', borderBottom: '1px solid #f4f4f4' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.86rem' }}>{u.name}</div>
                                    <div style={{ color: '#aaa', fontSize: '0.72rem' }}>{u.email}</div>
                                </div>
                                <select value={r || 'none'} onChange={(e) => void setRole(u.id, e.target.value)}
                                    style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.8rem', background: r === 'manager' ? '#eef2ff' : r === 'voditelj' ? '#fdf6e8' : r === 'provjera_uplate' ? '#eefaf0' : '#fff' }}>
                                    {(['none', 'user', 'voditelj', 'manager', 'provjera_uplate'] as const).map((opt) => (
                                        <option key={opt} value={opt}>{ROLE_LABELS[opt][locale === 'en' ? 1 : 0]}</option>
                                    ))}
                                </select>
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* ── Voditelj → regije ─────────────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>🗺️ {t('Voditelj → regije', 'Lead → regions')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>
                    {t('Regionalni prečac: voditelj automatski pokriva SVE poslovnice odabranih regija (i buduće, bez ručne dopune). Zbraja se s pojedinačnim poslovnicama ispod.',
                        'Regional shortcut: the lead automatically covers ALL branches of the selected regions (incl. future ones, no manual updates). Adds up with individual branches below.')}
                </div>
                {voditelji.length === 0 ? (
                    <div style={{ color: '#999', fontSize: '0.82rem' }}>{t('Nijedan korisnik nema ulogu „Voditelj". Dodijeli je gore.', 'No user has the "Lead" role yet. Assign it above.')}</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {voditelji.map((v) => {
                            const selected = new Set(data.voditelj_regions.filter((x) => x.voditelj_user_id === v.id).map((x) => x.region));
                            const toggle = (region: string) => {
                                const next = new Set(selected);
                                next.has(region) ? next.delete(region) : next.add(region);
                                void syncVoditeljRegions(v.id, Array.from(next));
                            };
                            return (
                                <div key={v.id} style={{ borderLeft: '3px solid #cfe0ea', paddingLeft: 12 }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.86rem', marginBottom: 6 }}>{v.name}</div>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {data.regions.map((region) => {
                                            const on = selected.has(region);
                                            return (
                                                <button key={region} type="button" onClick={() => toggle(region)}
                                                    style={{ padding: '4px 10px', borderRadius: 14, border: '1px solid ' + (on ? '#1e5c8a' : '#ccc'), background: on ? '#1e5c8a' : '#fff', color: on ? '#fff' : '#666', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                                                    {region}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* ── Voditelj → poslovnice ─────────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>🏬 {t('Voditelj → poslovnice', 'Lead → branches')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>
                    {t('Za svakog voditelja odaberi DODATNE pojedinačne poslovnice (izvan regije gore) koje odobrava. Zahtjev ide voditelju ako je tip označen i njegova je poslovnica na popisu ILI u njegovoj regiji.',
                        'For each lead pick ADDITIONAL individual branches (beyond the region above) they approve. A request routes to the lead if the type requires it and the branch is listed OR in their region.')}
                </div>
                {voditelji.length === 0 ? (
                    <div style={{ color: '#999', fontSize: '0.82rem' }}>{t('Nijedan korisnik nema ulogu „Voditelj". Dodijeli je gore.', 'No user has the "Lead" role yet. Assign it above.')}</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {voditelji.map((v) => {
                            const branchIds = data.voditelj_branches.filter((x) => x.voditelj_user_id === v.id).map((x) => x.branch_dept_id);
                            return (
                                <div key={v.id} style={{ borderLeft: '3px solid #e6cf9a', paddingLeft: 12 }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.86rem', marginBottom: 6 }}>{v.name}</div>
                                    <BranchMultiSelect branches={data.branches} selected={branchIds} locale={locale}
                                        onChange={(ids) => void syncVoditeljBranches(v.id, ids)} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            <DelegationsCard
                token={token}
                locale={locale}
                users={data.users}
                voditeljIds={data.users.filter((u) => data.roles[String(u.id)] === 'voditelj' || data.voditelj_branches.some((v) => v.voditelj_user_id === u.id)).map((u) => u.id)}
            />

            </>) : null}
            {section === 'odobrenja' ? (<>
            {/* ── Tipovi zahtjeva ───────────────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>🧾 {t('Tipovi zahtjeva', 'Request types')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>{t('Za koje tipove je potrebno odobrenje voditelja prije uprave.', 'Which types need a lead approval before management.')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {data.reason_codes.map((rc) => (
                        <label key={rc} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.86rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!reqVoditelj[rc]}
                                onChange={(e) => void saveSetting('type_requires_voditelj', { ...reqVoditelj, [rc]: e.target.checked })} />
                            <span>{(REASON_LABELS[rc] ?? [rc, rc])[locale === 'en' ? 1 : 0]}</span>
                            <code style={{ color: '#bbb', fontSize: '0.72rem' }}>{rc}</code>
                        </label>
                    ))}
                </div>
            </Card>

            {/* ── Posebna odobrenja (prag) ──────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>⭐ {t('Posebna odobrenja — veliki otkupi', 'Special approvals — large buys')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>
                    {t('Otkup (lom/investicijski) iznad praga traži dodatno odobrenje navedenih osoba (npr. Slaven Sviličić).',
                        'A buy (scrap/investment) above the threshold needs extra approval from the listed people (e.g. Slaven Sviličić).')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.84rem' }}>{t('Prag (€):', 'Threshold (€):')}</span>
                    <input type="number" defaultValue={Number(data.settings.extra_approval_threshold_eur ?? 50000)}
                        onBlur={(e) => void saveSetting('extra_approval_threshold_eur', Number(e.target.value) || 0)}
                        style={{ width: 140, padding: 6, border: '1px solid #ccc', borderRadius: 6 }} />
                </div>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 4 }}>{t('Odobravatelji:', 'Approvers:')}</div>
                <UserMultiSelect users={data.users} selected={(Array.isArray(data.settings.extra_approver_user_ids) ? data.settings.extra_approver_user_ids : []).map(Number)} locale={locale}
                    placeholder={t('Dodaj odobravatelja…', 'Add approver…')}
                    onChange={(ids) => void saveSetting('extra_approver_user_ids', ids)} />
            </Card>

            {/* ── HNWI ──────────────────────────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>💎 {t('HNWI podnositelji', 'HNWI requesters')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>
                    {t('Zahtjevi ovih podnositelja uvijek traže posebno odobrenje navedenih osoba (Vlado + Slaven), neovisno o iznosu.',
                        'Requests from these people always need special approval from the listed approvers (Vlado + Slaven), regardless of amount.')}
                </div>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 4 }}>{t('Podnositelji (HNWI):', 'Requesters (HNWI):')}</div>
                <UserMultiSelect users={data.users} selected={(Array.isArray(data.settings.hnwi_user_ids) ? data.settings.hnwi_user_ids : []).map(Number)} locale={locale}
                    placeholder={t('Dodaj HNWI podnositelja…', 'Add HNWI requester…')}
                    onChange={(ids) => void saveSetting('hnwi_user_ids', ids)} />
                <div style={{ fontSize: '0.82rem', fontWeight: 600, margin: '12px 0 4px' }}>{t('Odobravatelji HNWI:', 'HNWI approvers:')}</div>
                <UserMultiSelect users={data.users} selected={(Array.isArray(data.settings.hnwi_approver_user_ids) ? data.settings.hnwi_approver_user_ids : []).map(Number)} locale={locale}
                    placeholder={t('Dodaj odobravatelja…', 'Add approver…')}
                    onChange={(ids) => void saveSetting('hnwi_approver_user_ids', ids)} />
            </Card>

            </>) : null}
            {section === 'racuni' ? (<>
            <BranchAccountsCard token={token} locale={locale} data={data} reload={load} flash={flash} />

            {/* ── Računi (isplatni) ─────────────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>🏦 {t('Isplatni računi', 'Payout accounts')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>
                    {t('Isplata ide s NAŠEG računa iste banke kao klijent (bez naknade). ⭐ = zadani (fallback) račun firme kad klijentova banka nije među našima. Jedan fallback po firmi.',
                        'Payout goes from OUR account at the same bank as the client (no fee). ⭐ = the company default (fallback) when the client bank is not among ours. One fallback per company.')}
                </div>
                {(() => {
                    const byCompany = new Map<number, PtBankAccount[]>();
                    data.bank_accounts.filter((a) => a.is_active).forEach((a) => {
                        const cid = a.company_id ?? 0;
                        if (!byCompany.has(cid)) byCompany.set(cid, []);
                        byCompany.get(cid)!.push(a);
                    });
                    const companyName = (id: number) => data.companies.find((c) => c.id === id)?.code ?? `#${id}`;
                    const bankLabel = (a: PtBankAccount) => a.bank_code ? (data.bank_codes.find((bc) => bc.code === a.bank_code)?.short ?? a.bank_name) : a.bank_name;
                    return Array.from(byCompany.entries()).map(([cid, accs]) => (
                        <div key={cid} style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--ad-ink)', marginBottom: 4 }}>{companyName(cid)}</div>
                            <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                                {accs.map((a) => (
                                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 10px', borderBottom: '1px solid #f4f4f4', fontSize: '0.82rem', background: a.is_fallback ? '#fffdf0' : 'transparent' }}>
                                        <span style={{ minWidth: 54, fontWeight: 700, color: '#6b4f1c' }}>{bankLabel(a)}</span>
                                        <span style={{ color: '#888', fontSize: '0.74rem' }}>{a.purpose}</span>
                                        <code style={{ flex: 1, color: '#555' }}>…{a.iban.slice(-8)}</code>
                                        <button type="button" onClick={() => void toggleFallback(a.id, a.company_id, !a.is_fallback)}
                                            title={t('Postavi kao zadani (fallback) isplatni račun firme', 'Set as company default (fallback) payout account')}
                                            style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: '1rem', opacity: a.is_fallback ? 1 : 0.28 }}>⭐</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ));
                })()}
            </Card>

            {/* ── Šifrarnik banaka ──────────────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>🏛 {t('Šifrarnik banaka', 'Bank codes')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>
                    {t('7-znamenkasti kod iz IBAN-a (HRkk[kod]…) → banka. Koristi se za autodetekciju banke klijenta i odabir isplatnog računa.',
                        '7-digit code from the IBAN (HRkk[code]…) → bank. Used to auto-detect the client bank and pick the payout account.')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.bank_codes.map((bc) => (
                        <span key={bc.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 8, background: bc.is_active ? '#eef2ff' : '#f0f0f0', border: '1px solid #dfe4f2', fontSize: '0.78rem' }}>
                            <code style={{ fontWeight: 700, color: '#1e3a8a' }}>{bc.code}</code>
                            <strong>{bc.short ?? ''}</strong>
                            <span style={{ color: '#888' }}>{bc.name}</span>
                        </span>
                    ))}
                </div>
            </Card>

            </>) : null}
            {section === 'alarm' ? (<>
            {/* ── Radno vrijeme + alarm ─────────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>⏰ {t('Radno vrijeme + alarm', 'Work hours + alarm')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>
                    {t('Ako u radno vrijeme čekaju zahtjevi a nijedan manager nije prisutan na modulu → alarm mail (svakih 5 min, dedupe 30 min).',
                        'If requests are waiting during work hours and no manager is present on the module → alarm email (every 5 min, 30-min dedupe).')}
                </div>
                {(() => {
                    const wh = (data.settings.work_hours ?? { days: [1, 2, 3, 4, 5], from: '08:00', to: '20:00' }) as { days: number[]; from: string; to: string };
                    const dayLabels = locale === 'en' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] : ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'];
                    const days = new Set((wh.days ?? []).map(Number));
                    const setWh = (patch: Partial<typeof wh>) => void saveSetting('work_hours', { ...wh, ...patch });
                    return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                                {dayLabels.map((d, i) => {
                                    const iso = i + 1; const on = days.has(iso);
                                    return (
                                        <button key={iso} type="button" onClick={() => { const n = new Set(days); on ? n.delete(iso) : n.add(iso); setWh({ days: Array.from(n).sort() }); }}
                                            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid ' + (on ? '#1f7a36' : '#ccc'), background: on ? '#1f7a36' : '#fff', color: on ? '#fff' : '#666', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer' }}>{d}</button>
                                    );
                                })}
                            </div>
                            <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>{t('Od', 'From')}
                                <input type="time" defaultValue={wh.from} onBlur={(e) => setWh({ from: e.target.value })} style={{ padding: 4, border: '1px solid #ccc', borderRadius: 6 }} /></label>
                            <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>{t('Do', 'To')}
                                <input type="time" defaultValue={wh.to} onBlur={(e) => setWh({ to: e.target.value })} style={{ padding: 4, border: '1px solid #ccc', borderRadius: 6 }} /></label>
                        </div>
                    );
                })()}
                <div style={{ fontSize: '0.82rem', fontWeight: 600, margin: '14px 0 4px' }}>{t('Alarm primatelji (email):', 'Alarm recipients (email):')}</div>
                <EmailListField value={(data.settings.alarm_recipients ?? []) as string[]} locale={locale} onChange={(v) => void saveSetting('alarm_recipients', v)} />
            </Card>

            {/* ── Kontrola otkupa ───────────────────────────────── */}
            <Card>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>🔍 {t('Kontrola otkupa', 'Buy reconciliation')}</div>
                <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 10 }}>
                    {t('Dnevno (17:45) uparuje otkup zahtjeve s feedom bi_buyback_transactions (tip + iznos ±tolerancija + poslovnica + datum ±1). Nesparene šalje na primatelje.',
                        'Daily (17:45) matches buy requests to the bi_buyback_transactions feed (type + amount ±tolerance + branch + date ±1). Unmatched go to recipients.')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.84rem' }}>{t('Tolerancija iznosa (€):', 'Amount tolerance (€):')}</span>
                    <input type="number" defaultValue={Number(data.settings.reconcile_tolerance_eur ?? 10)}
                        onBlur={(e) => void saveSetting('reconcile_tolerance_eur', Number(e.target.value) || 0)}
                        style={{ width: 100, padding: 6, border: '1px solid #ccc', borderRadius: 6 }} />
                </div>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 4 }}>{t('Primatelji izvještaja (email):', 'Report recipients (email):')}</div>
                <EmailListField value={(data.settings.reconcile_recipients ?? []) as string[]} locale={locale} onChange={(v) => void saveSetting('reconcile_recipients', v)} />
            </Card>

            </>) : null}
            </div>
        </div>
    );
}

/** Jednostavan unos liste emailova (chips + dodaj). */
function EmailListField({ value, onChange, locale }: { value: string[]; onChange: (v: string[]) => void; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [input, setInput] = useState('');
    const add = () => {
        const e = input.trim().replace(/[,;]$/, '');
        if (e && /.+@.+\..+/.test(e) && !value.includes(e)) { onChange([...value, e]); setInput(''); }
    };
    return (
        <div>
            {value.length > 0 ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    {value.map((e) => (
                        <span key={e} style={{ background: '#fdecec', color: '#8a2b2b', borderRadius: 14, padding: '3px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {e}<button type="button" onClick={() => onChange(value.filter((x) => x !== e))} style={{ background: 'transparent', border: 0, color: '#8a2b2b', cursor: 'pointer', fontWeight: 800 }}>×</button>
                        </span>
                    ))}
                </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8 }}>
                <input type="email" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
                    placeholder={t('email + Enter', 'email + Enter')} style={{ flex: 1, maxWidth: 320, padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.84rem' }} />
                <button type="button" onClick={add} style={{ padding: '6px 12px', border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>＋ {t('Dodaj', 'Add')}</button>
            </div>
        </div>
    );
}

const BRANCH_ACCOUNT_MAX = 3;
const BRANCH_ACCOUNT_COMPANIES = ['ADO', 'ADMS'];
const PURPOSE_LABELS: Record<string, [string, string]> = {
    sub_zlato: ['Podračun zlato', 'Gold sub-account'],
    sub_mjenjacnica: ['Podračun mjenjačnica', 'Exchange sub-account'],
    unified: ['Objedinjeni', 'Unified'],
    other: ['Ostalo', 'Other'],
};

const BTN: React.CSSProperties = { padding: '4px 12px', fontSize: '0.8rem', fontWeight: 700, borderRadius: 6, lineHeight: 1.3, cursor: 'pointer' };
const BTN_QUIET: React.CSSProperties = { ...BTN, background: '#f4efe3', border: '1px solid #ddd5bd', color: '#2f3337' };
const INPUT: React.CSSProperties = { padding: '5px 9px', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.82rem', boxSizing: 'border-box' };

/** Desni drawer (isti obrazac kao m.40.9). Esc i klik na pozadinu zatvaraju. */
function SideDrawer({ title, subtitle, onClose, children }: { title: string; subtitle?: string; onClose: () => void; children: React.ReactNode }) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000 }} onClick={onClose}>
            <div role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}
                style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 520, maxWidth: '96vw', background: '#fff', borderLeft: '1px solid #ddd5bd', boxShadow: '-6px 0 18px rgba(0,0,0,0.12)', padding: 18, overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div>
                        <strong style={{ fontSize: '1rem' }}>{title}</strong>
                        {subtitle ? <div style={{ color: '#999', fontSize: '0.74rem', marginTop: 2 }}>{subtitle}</div> : null}
                    </div>
                    <button type="button" onClick={onClose} aria-label="Zatvori" style={{ ...BTN_QUIET, marginLeft: 'auto', padding: '2px 9px' }}>✕</button>
                </div>
                {children}
            </div>
        </div>
    );
}

/** Računi jedne poslovnice kod jedne firme (do tri) + unos novog. Prikazuje se u drawer-u. */
function BranchCompanyEditor({ token, locale, branch, company, accounts, bankLabel, reload, flash }: {
    token: string; locale: Locale; branch: PtBranch; company: PtCompany; accounts: PtBankAccount[];
    bankLabel: (a: PtBankAccount) => string; reload: () => Promise<void>; flash: (m: string) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [iban, setIban] = useState('');
    const [purpose, setPurpose] = useState('sub_zlato');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const full = accounts.length >= BRANCH_ACCOUNT_MAX;
    const run = async (fn: () => Promise<unknown>, ok: string) => {
        setBusy(true); setErr('');
        try { await fn(); flash(ok); await reload(); }
        catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
        finally { setBusy(false); }
    };
    const add = () => run(async () => {
        await mutate('/payment-traffic/settings/branch-accounts', token, 'POST', { branch_dept_id: branch.id, company_id: company.id, iban, purpose });
        setIban('');
    }, t('Račun dodan', 'Account added'));
    return (
        <section style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <strong style={{ fontSize: '0.92rem' }}>{company.code}</strong>
                <span style={{ color: '#999', fontSize: '0.76rem' }}>{accounts.length}/{BRANCH_ACCOUNT_MAX}</span>
            </div>
            {accounts.length > BRANCH_ACCOUNT_MAX ? (
                <div style={{ color: '#b14040', fontSize: '0.76rem', marginBottom: 6 }}>{t('Više od tri aktivna računa. Ukloni višak.', 'More than three active accounts. Remove the surplus.')}</div>
            ) : null}
            {accounts.length === 0 ? <div style={{ color: '#b14040', fontSize: '0.8rem', marginBottom: 8 }}>{t('Nema računa', 'No account')}</div> : null}
            {accounts.map((a) => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f4f4f4', flexWrap: 'wrap' }}>
                    <span style={{ minWidth: 52, fontWeight: 700, color: '#6b4f1c', fontSize: '0.82rem' }}>{bankLabel(a)}</span>
                    <code style={{ color: '#333', fontSize: '0.8rem' }}>{a.iban}</code>
                    <span style={{ color: '#888', fontSize: '0.72rem' }}>{(PURPOSE_LABELS[a.purpose] ?? [a.purpose, a.purpose])[locale === 'en' ? 1 : 0]}</span>
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.76rem', cursor: 'pointer' }}
                            title={t('Zadani račun za otkup i dnevnu gotovinu ove poslovnice kod te firme', 'Default account for buys and daily cash of this branch at this company')}>
                            <input type="checkbox" checked={!!a.is_buyback_default} disabled={busy}
                                onChange={(e) => void run(() => mutate(`/payment-traffic/settings/branch-accounts/${a.id}`, token, 'PATCH', { is_buyback_default: e.target.checked }), t('Zadani račun spremljen', 'Default saved'))} />
                            {t('zadani', 'default')}
                        </label>
                        <button type="button" disabled={busy} title={t('Ukloni iz postavki (račun postaje neaktivan)', 'Remove (account becomes inactive)')}
                            onClick={() => { if (confirm(t(`Ukloniti račun ${a.iban}?`, `Remove account ${a.iban}?`))) void run(() => mutate(`/payment-traffic/settings/branch-accounts/${a.id}`, token, 'DELETE'), t('Račun uklonjen', 'Account removed')); }}
                            style={{ ...BTN_QUIET, padding: '2px 9px', color: '#b14040' }}>{t('Ukloni', 'Remove')}</button>
                    </span>
                </div>
            ))}
            {!full ? (
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    <input type="text" value={iban} onChange={(e) => setIban(e.target.value)} placeholder="IBAN"
                        style={{ ...INPUT, flex: '1 1 220px', minWidth: 0, fontFamily: 'monospace' }} />
                    <select value={purpose} onChange={(e) => setPurpose(e.target.value)} style={INPUT}>
                        {Object.keys(PURPOSE_LABELS).map((k) => <option key={k} value={k}>{PURPOSE_LABELS[k][locale === 'en' ? 1 : 0]}</option>)}
                    </select>
                    <button type="button" disabled={busy || iban.trim().length < 15} onClick={() => void add()}
                        style={{ ...BTN_QUIET, opacity: busy || iban.trim().length < 15 ? 0.5 : 1 }}>{t('Dodaj', 'Add')}</button>
                </div>
            ) : <div style={{ color: '#999', fontSize: '0.74rem', marginTop: 8 }}>{t('Dosegnut je najviše tri računa. Ukloni jedan za novi unos.', 'Limit of three reached. Remove one to add another.')}</div>}
            {err ? <div style={{ color: '#b14040', fontSize: '0.76rem', marginTop: 6 }}>{err}</div> : null}
        </section>
    );
}

type AccFilter = 'all' | 'missing_ado' | 'missing_adms' | 'no_default';

/**
 * Računi poslovnica po firmi (ADO, ADMS), do tri po paru. Piše u bi_bank_accounts, isti šifrarnik
 * koji čita m.100 i isplatni resolver, pa nema druge kopije. Tablica je pregled, uređuje se u drawer-u.
 */
function BranchAccountsCard({ token, locale, data, reload, flash }: {
    token: string; locale: Locale; data: ShowResp; reload: () => Promise<void>; flash: (m: string) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState<AccFilter>('all');
    const [openId, setOpenId] = useState<number | null>(null);
    const companies = BRANCH_ACCOUNT_COMPANIES
        .map((code) => data.companies.find((c) => c.code === code))
        .filter((c): c is PtCompany => !!c);
    const bankLabel = (a: PtBankAccount) => a.bank_code ? (data.bank_codes.find((bc) => bc.code === a.bank_code)?.short ?? a.bank_name) : a.bank_name;
    const accountsFor = useCallback((branchId: number, companyId: number) => data.bank_accounts.filter((a) =>
        a.branch_dept_id === branchId && a.company_id === companyId && !!a.is_active && a.lifecycle_status === 'active'), [data.bank_accounts]);
    const missingFor = useCallback((b: PtBranch, code: string) => {
        const c = companies.find((x) => x.code === code);
        return !!c && accountsFor(b.id, c.id).length === 0;
    }, [companies, accountsFor]);
    const noDefault = useCallback((b: PtBranch) => companies.some((c) => {
        const acc = accountsFor(b.id, c.id);
        return acc.length > 1 && !acc.some((a) => !!a.is_buyback_default);
    }), [companies, accountsFor]);
    const counts = useMemo(() => ({
        all: data.branches.length,
        missing_ado: data.branches.filter((b) => missingFor(b, 'ADO')).length,
        missing_adms: data.branches.filter((b) => missingFor(b, 'ADMS')).length,
        no_default: data.branches.filter((b) => noDefault(b)).length,
    }), [data.branches, missingFor, noDefault]);
    const rows = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return data.branches.filter((b) => {
            if (needle && !(`${b.name} ${b.code}`.toLowerCase().includes(needle))) return false;
            if (filter === 'missing_ado') return missingFor(b, 'ADO');
            if (filter === 'missing_adms') return missingFor(b, 'ADMS');
            if (filter === 'no_default') return noDefault(b);
            return true;
        });
    }, [data.branches, q, filter, missingFor, noDefault]);
    const open = openId != null ? data.branches.find((b) => b.id === openId) ?? null : null;
    const filters: Array<[AccFilter, string]> = [
        ['all', t('Sve', 'All')],
        ['missing_ado', t('Bez ADO računa', 'No ADO account')],
        ['missing_adms', t('Bez ADMS računa', 'No ADMS account')],
        ['no_default', t('Više računa bez zadanog', 'Several accounts, no default')],
    ];
    const cell = (b: PtBranch, c: PtCompany) => {
        const acc = accountsFor(b.id, c.id);
        if (acc.length === 0) return <span style={{ color: '#b14040', fontSize: '0.76rem' }}>{t('nema računa', 'no account')}</span>;
        return (
            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                {acc.map((a) => (
                    <span key={a.id} style={{ fontSize: '0.78rem', display: 'inline-flex', gap: 6, alignItems: 'baseline', whiteSpace: 'nowrap' }}>
                        <span style={{ minWidth: 44, fontWeight: 700, color: '#6b4f1c' }}>{bankLabel(a)}</span>
                        <code style={{ color: '#444' }}>…{a.iban.slice(-8)}</code>
                        {a.is_buyback_default ? <span style={{ color: '#6b4f1c', fontSize: '0.7rem', fontWeight: 700 }}>{t('zadani', 'default')}</span> : null}
                    </span>
                ))}
            </span>
        );
    };
    const th: React.CSSProperties = { textAlign: 'left', padding: '7px 12px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#818686', borderBottom: '1px solid #e3e3e3', background: '#fafaf7', position: 'sticky', top: 0 };
    const td: React.CSSProperties = { padding: '7px 12px', borderBottom: '1px solid #f4f4f4', verticalAlign: 'top' };
    return (
        <Card pad={16}>
            <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>{t('Računi poslovnica po firmi', 'Branch accounts by company')}</div>
            <div style={{ color: '#777', fontSize: '0.8rem', marginBottom: 12 }}>
                {t('Do tri računa po poslovnici kod ADO i kod ADMS. Unos ide izravno u šifrarnik računa (m.100), jedini izvor za isplatu, dnevnu gotovinu i otkup. Zadani račun treba samo kad poslovnica kod firme ima više računa.',
                    'Up to three accounts per branch at ADO and at ADMS. Entries go straight to the account codebook (m.100), the single source for payouts, daily cash and buys. A default is only needed when a branch has several accounts at a company.')}
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Traži poslovnicu…', 'Search branch…')} style={{ ...INPUT, width: 240 }} />
                <div className="bi-seg" role="group">
                    {filters.map(([k, label]) => (
                        <button key={k} type="button" className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}
                            style={{ padding: '4px 12px', fontSize: '0.78rem' }}>
                            {label} <span style={{ opacity: 0.6, fontWeight: 400 }}>{counts[k]}</span>
                        </button>
                    ))}
                </div>
                <span style={{ color: '#999', fontSize: '0.76rem' }}>{rows.length}/{data.branches.length}</span>
            </div>
            <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={th}>{t('Poslovnica', 'Branch')}</th>
                            {companies.map((c) => <th key={c.id} style={th}>{c.code}</th>)}
                            <th style={{ ...th, width: 1 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((b) => (
                            <tr key={b.id} onClick={() => setOpenId(b.id)} style={{ cursor: 'pointer' }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#faf7ee'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                                <td style={td}>
                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}>{b.name}</div>
                                    <div style={{ color: '#aaa', fontSize: '0.7rem' }}>{b.code}</div>
                                </td>
                                {companies.map((c) => <td key={c.id} style={td}>{cell(b, c)}</td>)}
                                <td style={{ ...td, textAlign: 'right' }}><button type="button" style={BTN_QUIET} onClick={(e) => { e.stopPropagation(); setOpenId(b.id); }}>{t('Uredi', 'Edit')}</button></td>
                            </tr>
                        ))}
                        {rows.length === 0 ? <tr><td colSpan={companies.length + 2} style={{ padding: 14, color: '#999', fontSize: '0.82rem' }}>{t('Nema poslovnica za ovaj filter.', 'No branches for this filter.')}</td></tr> : null}
                    </tbody>
                </table>
            </div>
            {open ? (
                <SideDrawer title={open.name} subtitle={open.code} onClose={() => setOpenId(null)}>
                    {companies.map((c) => (
                        <BranchCompanyEditor key={c.id} token={token} locale={locale} branch={open} company={c}
                            accounts={accountsFor(open.id, c.id)} bankLabel={bankLabel} reload={reload} flash={flash} />
                    ))}
                </SideDrawer>
            ) : null}
        </Card>
    );
}
